// Admin Service - Kullanıcı ve Moderasyon Yönetimi
import { 
  collection, 
  getDocs, 
  doc, 
  getDoc,
  updateDoc,
  setDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  onSnapshot,
  arrayUnion
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase';
import { deleteFile } from './storage';
import { createNotification, NOTIFICATION_TYPES } from './notifications';
import { recordApprovalLog, AUDIT_ENTITY, AUDIT_ACTION } from './auditLog';
import logger from '../utils/logger';
import { initStoreOwner } from './storeMembers';

/**
 * Tüm kullanıcıları getirir (Admin yetkisi gerekir)
 */
export const getAllUsers = async () => {
  try {
    logger.log('👥 Tüm kullanıcılar getiriliyor...');
    
    const usersRef = collection(db, 'users');
    const querySnapshot = await getDocs(usersRef);
    
    const users = [];
    querySnapshot.forEach((doc) => {
      users.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    logger.log(`✅ ${users.length} kullanıcı getirildi`);
    return { success: true, data: users };
  } catch (error) {
    logger.error('❌ Kullanıcılar getirilirken hata:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Tüm satıcı profillerini getirir
 */
export const getAllSellerProfiles = async () => {
  try {
    logger.log('🏪 Tüm satıcı profilleri getiriliyor...');
    
    const sellersRef = collection(db, 'sellerProfiles');
    const querySnapshot = await getDocs(sellersRef);
    
    const sellers = [];
    for (const docSnap of querySnapshot.docs) {
      const sellerData = docSnap.data();
      
      // User bilgilerini de al
      const userDoc = await getDoc(doc(db, 'users', docSnap.id));
      const userData = userDoc.exists() ? userDoc.data() : {};
      
      sellers.push({
        id: docSnap.id,
        ...sellerData,
        userEmail: userData.email,
        userName: `${userData.firstName || ''} ${userData.lastName || ''}`.trim(),
        userPhone: sellerData.phone || userData.phone || 'Belirtilmemiş'
      });
    }
    
    logger.log(`✅ ${sellers.length} satıcı profili getirildi`);
    return { success: true, data: sellers };
  } catch (error) {
    logger.error('❌ Satıcı profilleri getirilirken hata:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Kullanıcının rollerini günceller
 */
export const updateUserRoles = async (userId, newRoles) => {
  try {
    logger.log(`🔧 Kullanıcı rolleri güncelleniyor: ${userId}`);
    
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      roles: newRoles
    });
    
    logger.log(`✅ Roller güncellendi: ${newRoles.join(', ')}`);
    return { success: true };
  } catch (error) {
    logger.error('❌ Roller güncellenirken hata:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Satıcının aktif/pasif durumunu değiştirir
 */
export const toggleSellerStatus = async (sellerId, isActive) => {
  try {
    logger.log(`🔧 Satıcı durumu değiştiriliyor: ${sellerId} -> ${isActive}`);
    
    const sellerRef = doc(db, 'sellerProfiles', sellerId);
    await updateDoc(sellerRef, {
      isActive: isActive,
      updatedAt: new Date().toISOString()
    });
    
    logger.log(`✅ Satıcı durumu güncellendi: ${isActive ? 'Aktif' : 'Pasif'}`);
    return { success: true };
  } catch (error) {
    logger.error('❌ Satıcı durumu değiştirilirken hata:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Satıcı profilini siler (Kullanıcı hesabını silmez, sadece seller rolünü kaldırır)
 */
export const deleteSeller = async (sellerId) => {
  try {
    logger.warn(`⚠️ SATICI PROFİLİ SİLİNİYOR: ${sellerId}`);
    
    let deletedItemsCount = {
      products: 0,
      orders: 0,
      images: 0
    };
    
    // 1. Satıcı profilini al
    const sellerRef = doc(db, 'sellerProfiles', sellerId);
    const sellerDoc = await getDoc(sellerRef);
    
    if (!sellerDoc.exists()) {
      return { success: false, error: 'Satıcı profili bulunamadı' };
    }
    
    const sellerData = sellerDoc.data();
    
    // 2. Satıcının tüm ürünlerini sil
    logger.log('🔍 Satıcının ürünleri aranıyor...');
    logger.log('🔑 SellerId:', sellerId);
    
    const productsQuery = query(
      collection(db, 'products'),
      where('sellerId', '==', sellerId)
    );
    
    const productsSnapshot = await getDocs(productsQuery);
    logger.log(`📦 ${productsSnapshot.size} ürün bulundu`);
    
    if (productsSnapshot.empty) {
      logger.log('ℹ️ Bu satıcının ürünü yok (veya zaten silinmiş)');
    }
    
    for (const productDoc of productsSnapshot.docs) {
      const productData = productDoc.data();
      
      // Ürün görsellerini sil
      if (productData.images && Array.isArray(productData.images)) {
        for (const imageUrl of productData.images) {
          try {
            await deleteFile(imageUrl);
            deletedItemsCount.images++;
            logger.log('🗑️ Ürün görseli silindi:', imageUrl);
          } catch (err) {
            logger.warn('⚠️ Görsel silinemedi:', err.message);
          }
        }
      }
      
      // 3D dosyası varsa sil
      if (productData.fileUrl) {
        try {
          await deleteFile(productData.fileUrl);
          logger.log('🗑️ 3D dosya silindi:', productData.fileUrl);
        } catch (err) {
          logger.warn('⚠️ 3D dosya silinemedi:', err.message);
        }
      }
      
      // Ürünü sil
      await deleteDoc(productDoc.ref);
      deletedItemsCount.products++;
      logger.log(`🗑️ Ürün silindi: ${productData.name}`);
    }
    
    // 3. Satıcının siparişlerini kontrol et (opsiyonel: sil veya işaretle)
    // Not: Siparişler genellikle SAKLANmalı (muhasebe/veri için)
    // Ama isterseniz silebiliriz
    const ordersQuery = query(
      collection(db, 'orders'),
      where('sellerId', '==', sellerId)
    );
    
    const ordersSnapshot = await getDocs(ordersQuery);
    logger.log(`📋 ${ordersSnapshot.size} sipariş bulundu`);
    
    // Siparişleri silmek yerine "seller deleted" olarak işaretle
    for (const orderDoc of ordersSnapshot.docs) {
      await updateDoc(orderDoc.ref, {
        sellerDeleted: true,
        deletedAt: new Date().toISOString()
      });
      deletedItemsCount.orders++;
      logger.log('📝 Sipariş işaretlendi (satıcı silindi)');
    }
    
    // 4. Profil görseli varsa Storage'dan sil
    if (sellerData?.profileImageUrl) {
      logger.log('🗑️ Profil görseli siliniyor:', sellerData.profileImageUrl);
      try {
        const deleteResult = await deleteFile(sellerData.profileImageUrl);
        if (deleteResult.success) {
          logger.log('✅ Profil görseli silindi');
        } else {
          logger.warn('⚠️ Görsel silinemedi:', deleteResult.error);
        }
      } catch (deleteError) {
        logger.warn('⚠️ Görsel silinemedi:', deleteError.message);
      }
    }
    
    // Pending profil görseli varsa sil
    if (sellerData?.pendingProfileImageUrl) {
      try {
        await deleteFile(sellerData.pendingProfileImageUrl);
        logger.log('✅ Pending profil görseli silindi');
      } catch (err) {
        logger.warn('⚠️ Pending görsel silinemedi:', err.message);
      }
    }
    
    // 5. Satıcı profilini sil
    await deleteDoc(sellerRef);
    logger.log('🗑️ Satıcı profili silindi');
    
    // 6. Kullanıcıdan seller rolünü kaldır
    const userRef = doc(db, 'users', sellerId);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      const currentRoles = userData.roles || [];
      const newRoles = currentRoles.filter(role => role !== 'seller');
      
      // En az user rolü olmalı
      if (newRoles.length === 0) {
        newRoles.push('user');
      }
      
      await updateDoc(userRef, {
        roles: newRoles
      });
      logger.log('✅ Seller rolü kullanıcıdan kaldırıldı');
    }
    
    logger.log('\n✅ Satıcı profili başarıyla silindi!');
    logger.log(`📊 Silinen öğeler:`);
    logger.log(`   📦 ${deletedItemsCount.products} ürün`);
    logger.log(`   📋 ${deletedItemsCount.orders} sipariş (işaretlendi)`);
    logger.log(`   🖼️ ${deletedItemsCount.images} görsel`);
    
    return { 
      success: true,
      deletedItems: deletedItemsCount
    };
  } catch (error) {
    logger.error('❌ Satıcı profili silinirken hata:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Kullanıcıyı siler (Firestore + Firebase Auth)
 * Cloud Function üzerinden çalışır — aynı email ile tekrar kayıt olunabilir.
 */
export const deleteUser = async (userId) => {
  try {
    logger.warn(`⚠️ KULLANICI SİLİNİYOR: ${userId}`);

    const functions = getFunctions();
    const adminDeleteUser = httpsCallable(functions, 'adminDeleteUser');
    const result = await adminDeleteUser({ userId });

    if (result.data.success) {
      logger.log('✅ Kullanıcı tamamen silindi (Firestore + Auth)');
      return { success: true };
    } else {
      return { success: false, error: result.data.error || 'Silme işlemi başarısız.' };
    }
  } catch (error) {
    logger.error('❌ Kullanıcı silinirken hata:', error);
    const message = error?.message || 'Kullanıcı silinirken bir hata oluştu.';
    return { success: false, error: message };
  }
};

/**
 * Kullanıcıya admin rolü ekler
 */
export const makeAdmin = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      return { success: false, error: 'Kullanıcı bulunamadı' };
    }
    
    const currentRoles = userDoc.data().roles || ['user'];
    
    if (currentRoles.includes('admin')) {
      return { success: false, error: 'Kullanıcı zaten admin' };
    }
    
    const newRoles = [...currentRoles, 'admin'];
    await updateDoc(userRef, { roles: newRoles });
    
    logger.log(`✅ ${userId} admin yapıldı`);
    return { success: true };
  } catch (error) {
    logger.error('❌ Admin yapılırken hata:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Kullanıcıdan admin rolünü kaldırır
 */
export const removeAdmin = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      return { success: false, error: 'Kullanıcı bulunamadı' };
    }
    
    const currentRoles = userDoc.data().roles || [];
    const newRoles = currentRoles.filter(role => role !== 'admin');
    
    if (newRoles.length === 0) {
      newRoles.push('user'); // En az user rolü olmalı
    }
    
    await updateDoc(userRef, { roles: newRoles });
    
    logger.log(`✅ ${userId} admin rolü kaldırıldı`);
    return { success: true };
  } catch (error) {
    logger.error('❌ Admin rolü kaldırılırken hata:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Kullanıcıya moderator rolü ekler (sadece admin yapabilir)
 */
export const makeModerator = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      return { success: false, error: 'Kullanıcı bulunamadı' };
    }
    
    const currentRoles = userDoc.data().roles || ['user'];
    
    if (currentRoles.includes('moderator')) {
      return { success: false, error: 'Kullanıcı zaten moderator' };
    }
    
    const newRoles = [...currentRoles, 'moderator'];
    await updateDoc(userRef, { roles: newRoles });
    
    logger.log(`✅ ${userId} moderator yapıldı`);
    return { success: true };
  } catch (error) {
    logger.error('❌ Moderator yapılırken hata:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Kullanıcıdan moderator rolünü kaldırır (sadece admin yapabilir)
 */
export const removeModerator = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      return { success: false, error: 'Kullanıcı bulunamadı' };
    }
    
    const currentRoles = userDoc.data().roles || [];
    const newRoles = currentRoles.filter(role => role !== 'moderator');
    
    if (newRoles.length === 0) {
      newRoles.push('user'); // En az user rolü olmalı
    }
    
    await updateDoc(userRef, { roles: newRoles });
    
    logger.log(`✅ ${userId} moderator rolü kaldırıldı`);
    return { success: true };
  } catch (error) {
    logger.error('❌ Moderator rolü kaldırılırken hata:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Tüm siparişleri getirir
 */
export const getAllOrders = async () => {
  try {
    logger.log('🛒 Tüm siparişler getiriliyor...');
    
    const ordersRef = collection(db, 'orders');
    const querySnapshot = await getDocs(ordersRef);
    
    const orders = [];
    for (const docSnap of querySnapshot.docs) {
      const orderData = docSnap.data();
      
      // Alıcı bilgilerini al
      const buyerDoc = await getDoc(doc(db, 'users', orderData.userId));
      const buyerData = buyerDoc.exists() ? buyerDoc.data() : {};
      
      // Satıcı bilgilerini al
      const sellerDoc = await getDoc(doc(db, 'users', orderData.sellerId));
      const sellerData = sellerDoc.exists() ? sellerDoc.data() : {};
      
      orders.push({
        id: docSnap.id,
        ...orderData,
        buyerName: `${buyerData.firstName || ''} ${buyerData.lastName || ''}`.trim(),
        buyerEmail: buyerData.email,
        sellerName: `${sellerData.firstName || ''} ${sellerData.lastName || ''}`.trim(),
        sellerEmail: sellerData.email
      });
    }
    
    // Tarihe göre sırala (en yeni en üstte)
    orders.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    
    logger.log(`✅ ${orders.length} sipariş getirildi`);
    return { success: true, data: orders };
  } catch (error) {
    logger.error('❌ Siparişler getirilirken hata:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Sipariş durumunu günceller
 */
export const updateOrderStatus = async (orderId, newStatus) => {
  try {
    logger.log(`🔧 Sipariş durumu güncelleniyor: ${orderId} -> ${newStatus}`);
    
    const orderRef = doc(db, 'orders', orderId);
    await updateDoc(orderRef, {
      status: newStatus,
      updatedAt: new Date().toISOString()
    });
    
    logger.log(`✅ Sipariş durumu güncellendi: ${newStatus}`);
    return { success: true };
  } catch (error) {
    logger.error('❌ Sipariş durumu güncellenirken hata:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Siparişi siler
 */
export const deleteOrder = async (orderId) => {
  try {
    logger.warn(`⚠️ SİPARİŞ SİLİNİYOR: ${orderId}`);
    
    const orderRef = doc(db, 'orders', orderId);
    await deleteDoc(orderRef);
    
    logger.log('✅ Sipariş silindi');
    return { success: true };
  } catch (error) {
    logger.error('❌ Sipariş silinirken hata:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Tüm modelleri getirir
 */
/**
 * Tüm ürünleri (products) getirir
 * NOT: Eski "models" yerine artık "products" collection kullanılıyor
 */
export const getAllModels = async () => {
  try {
    logger.log('📦 Tüm ürünler getiriliyor (products collection)...');
    
    const productsRef = collection(db, 'products');
    const q = query(productsRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    
    const products = [];
    for (const docSnap of querySnapshot.docs) {
      const productData = docSnap.data();
      
      // Satıcı bilgilerini al
      let sellerName = 'Bilinmeyen';
      let sellerEmail = '';
      
      try {
        const sellerDoc = await getDoc(doc(db, 'sellerProfiles', productData.sellerId));
        if (sellerDoc.exists()) {
          const sellerData = sellerDoc.data();
          sellerName = sellerData.publicName || 'Bilinmeyen';
          
          // User email'i al
          const userDoc = await getDoc(doc(db, 'users', productData.sellerId));
          if (userDoc.exists()) {
            sellerEmail = userDoc.data().email || '';
          }
        }
      } catch (err) {
        logger.warn('Satıcı bilgisi alınamadı:', err);
      }
      
      products.push({
        id: docSnap.id,
        ...productData,
        title: productData.name, // Uyumluluk için
        name: productData.name,
        sellerName: sellerName,
        sellerEmail: sellerEmail,
        isApproved: productData.approvalStatus === 'approved' // Uyumluluk için
      });
    }
    
    logger.log(`✅ ${products.length} ürün getirildi`);
    return { success: true, data: products };
  } catch (error) {
    logger.error('❌ Ürünler getirilirken hata:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Ürün durumunu günceller (onaylanmış/reddedilmiş)
 * NOT: Eski "models" yerine "products" kullanılıyor
 */
export const updateModelStatus = async (productId, isApproved) => {
  try {
    logger.log(`🔧 Ürün durumu güncelleniyor: ${productId} -> ${isApproved ? 'Onaylandı' : 'Reddedildi'}`);
    
    const productRef = doc(db, 'products', productId);
    await updateDoc(productRef, {
      approvalStatus: isApproved ? 'approved' : 'rejected',
      isApproved: isApproved, // Uyumluluk için
      updatedAt: new Date().toISOString()
    });
    
    logger.log(`✅ Ürün durumu güncellendi`);
    return { success: true };
  } catch (error) {
    logger.error('❌ Ürün durumu güncellenirken hata:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Ürünü siler (görselleri ve 3D dosyalarıyla birlikte)
 * NOT: Eski "models" yerine "products" kullanılıyor
 */
export const deleteModel = async (productId) => {
  try {
    logger.warn(`⚠️ ÜRÜN SİLİNİYOR: ${productId}`);
    
    const productRef = doc(db, 'products', productId);
    const productSnap = await getDoc(productRef);
    
    if (!productSnap.exists()) {
      return { success: false, error: 'Ürün bulunamadı' };
    }
    
    const productData = productSnap.data();
    
    // Ürün görsellerini sil
    if (productData.images && Array.isArray(productData.images)) {
      for (const imageUrl of productData.images) {
        try {
          await deleteFile(imageUrl);
          logger.log('🗑️ Ürün görseli silindi:', imageUrl);
        } catch (err) {
          logger.warn('⚠️ Görsel silinemedi:', err.message);
        }
      }
    }
    
    // 3D dosyası varsa sil
    if (productData.fileUrl) {
      try {
        await deleteFile(productData.fileUrl);
        logger.log('🗑️ 3D dosya silindi:', productData.fileUrl);
      } catch (err) {
        logger.warn('⚠️ 3D dosya silinemedi:', err.message);
      }
    }
    
    // Ürünü sil
    await deleteDoc(productRef);
    
    logger.log('✅ Ürün ve tüm dosyaları silindi');
    return { success: true };
  } catch (error) {
    logger.error('❌ Ürün silinirken hata:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Bekleyen satıcı başvurularını getirir
 */
export const getPendingSellers = async () => {
  try {
    logger.log('⏳ Bekleyen satıcı başvuruları getiriliyor...');
    
    const sellersQuery = query(
      collection(db, 'sellerProfiles'),
      where('approvalStatus', '==', 'pending')
    );
    
    const querySnapshot = await getDocs(sellersQuery);
    const pendingSellers = [];
    
    for (const docSnap of querySnapshot.docs) {
      const sellerData = docSnap.data();
      
      // User bilgilerini al
      const userDoc = await getDoc(doc(db, 'users', docSnap.id));
      const userData = userDoc.exists() ? userDoc.data() : {};
      
      logger.log('📱 Seller Phone Debug:', {
        sellerId: docSnap.id,
        sellerDataPhone: sellerData.phone,
        userDataPhone: userData.phone,
        finalPhone: sellerData.phone || userData.phone || 'Belirtilmemiş'
      });
      
      pendingSellers.push({
        id: docSnap.id,
        ...sellerData,
        userEmail: userData.email,
        userName: `${userData.firstName || ''} ${userData.lastName || ''}`.trim(),
        userPhone: sellerData.phone || userData.phone || 'Belirtilmemiş'
      });
    }
    
    // Tarihe göre sırala (en yeni en üstte)
    pendingSellers.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    
    logger.log(`✅ ${pendingSellers.length} bekleyen başvuru bulundu`);
    return { success: true, data: pendingSellers };
  } catch (error) {
    logger.error('❌ Bekleyen başvurular getirilirken hata:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Satıcı başvurusunu onaylar ve seller rolünü ekler
 */
export const approveSeller = async (sellerId, adminId) => {
  try {
    logger.log(`✅ Satıcı başvurusu onaylanıyor: ${sellerId}`);
    
    // 1. Kullanıcıya seller rolü ekle
    const userRef = doc(db, 'users', sellerId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      logger.error('❌ Kullanıcı bulunamadı!');
      return { success: false, error: 'Kullanıcı bulunamadı' };
    }
    
    const userData = userDoc.data();
    const currentRoles = userData.roles || ['user'];
    
    // Zaten seller rolü varsa tekrar ekleme
    if (!currentRoles.includes('seller')) {
      logger.log('💾 Seller rolü ekleniyor...');
      await updateDoc(userRef, {
        roles: [...currentRoles, 'seller']
      });
      logger.log('✅ Seller rolü eklendi');
    }
    
    // 2. Seller profilini onayla
    const sellerRef = doc(db, 'sellerProfiles', sellerId);
    await updateDoc(sellerRef, {
      isActive: true,
      approvalStatus: 'approved',
      approvalDate: new Date().toISOString(),
      approvedBy: adminId,
      updatedAt: new Date().toISOString()
    });

    // Denetim kaydı: satıcı başvurusu onayı
    await recordApprovalLog({
      entityType: AUDIT_ENTITY.SELLER,
      entityId: sellerId,
      action: AUDIT_ACTION.APPROVED,
      actorId: adminId,
      sellerId,
      note: 'Satıcı başvurusu onaylandı'
    });

    // 3. Mağaza sahibini storeMembers'a ekle (ekip yönetimi için)
    try {
      await initStoreOwner(sellerId, sellerId);
      logger.log('✅ Store owner kaydı oluşturuldu');
    } catch (ownerError) {
      logger.warn('⚠️ Store owner kaydı oluşturulamadı:', ownerError.message);
    }

    // 4. Email bildirimi gönder
    try {
      logger.log('📧 Email bildirimi gönderiliyor...');
      const functions = getFunctions();
      const sendEmail = httpsCallable(functions, 'sendSellerApprovalEmail');
      
      await sendEmail({
        userEmail: userData.email,
        userName: `${userData.firstName} ${userData.lastName}`
      });
      
      logger.log('✅ Email bildirimi gönderildi');
    } catch (emailError) {
      logger.warn('⚠️ Email gönderilemedi (fonksiyon henüz deploy edilmemiş olabilir):', emailError.message);
      // Email hatası tüm işlemi engellemez
    }
    
    // 5. Satıcıya bildirim gönder
    try {
      await createNotification(
        sellerId,
        NOTIFICATION_TYPES.SELLER_APPROVED,
        'Satıcı Başvurunuz Onaylandı!',
        'Tebrikler! Satıcı başvurunuz onaylandı. Artık ürün ekleyebilirsiniz.',
        { link: '/my-store' }
      );
    } catch (notifError) {
      logger.warn('Satıcı onay bildirimi gönderilemedi:', notifError.message);
    }

    logger.log(`✅ Satıcı başvurusu onaylandı!`);
    return { success: true };
  } catch (error) {
    logger.error('❌ Satıcı onaylanırken hata:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Moderator'ın admin için "onay önerisi" notu eklemesi (moderator gerçek onay yapamaz, sadece admin'e not düşer)
 */
export const addModeratorRecommendation = async (sellerId, moderatorId, moderatorEmail, note = '') => {
  try {
    const sellerRef = doc(db, 'sellerProfiles', sellerId);
    await updateDoc(sellerRef, {
      moderatorRecommendation: {
        recommendedBy: moderatorId,
        recommendedByEmail: moderatorEmail || null,
        recommendedAt: serverTimestamp(),
        note: (note || '').trim() || null
      },
      updatedAt: new Date().toISOString()
    });
    logger.log(`🛡️ Moderator önerisi eklendi: ${sellerId}`);
    return { success: true, message: 'Admin için not kaydedildi.' };
  } catch (error) {
    logger.error('Moderator önerisi eklenirken hata:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Satıcı başvurusunu reddeder
 * @param {string} sellerId
 * @param {string} adminId
 * @param {string} reason - Not (opsiyonel)
 * @param {boolean} noteVisibleToUser - Notu kullanıcıya göster
 */
export const rejectSeller = async (sellerId, adminId, reason = '', noteVisibleToUser = false) => {
  try {
    logger.log(`❌ Satıcı başvurusu reddediliyor: ${sellerId}`);
    
    const sellerRef = doc(db, 'sellerProfiles', sellerId);
    const sellerDoc = await getDoc(sellerRef);
    const sellerData = sellerDoc.exists() ? sellerDoc.data() : null;
    
    if (sellerData?.profileImageUrl) {
      logger.log('🗑️ Profil görseli siliniyor:', sellerData.profileImageUrl);
      try {
        const deleteResult = await deleteFile(sellerData.profileImageUrl);
        if (deleteResult.success) logger.log('✅ Profil görseli silindi');
        else logger.warn('⚠️ Görsel silinemedi:', deleteResult.error);
      } catch (deleteError) {
        logger.warn('⚠️ Görsel silinemedi:', deleteError.message);
      }
    }
    
    const userRef = doc(db, 'users', sellerId);
    const userDoc = await getDoc(userRef);
    const userData = userDoc.exists() ? userDoc.data() : null;
    
    const displayReason = noteVisibleToUser && reason ? reason : 'Admin tarafından reddedildi';
    
    await updateDoc(sellerRef, {
      isActive: false,
      approvalStatus: 'rejected',
      approvalDate: new Date().toISOString(),
      approvedBy: adminId,
      rejectionReason: reason || 'Admin tarafından reddedildi',
      rejectionNoteVisibleToUser: !!noteVisibleToUser,
      updatedAt: new Date().toISOString()
    });

    // Denetim kaydı: satıcı başvurusu reddi
    await recordApprovalLog({
      entityType: AUDIT_ENTITY.SELLER,
      entityId: sellerId,
      action: AUDIT_ACTION.REJECTED,
      actorId: adminId,
      sellerId,
      note: reason || 'Satıcı başvurusu reddedildi'
    });

    if (userData) {
      try {
        logger.log('📧 Red bildirimi email gönderiliyor...');
        const functions = getFunctions();
        const sendEmail = httpsCallable(functions, 'sendSellerRejectionEmail');
        
        await sendEmail({
          userEmail: userData.email,
          userName: `${userData.firstName} ${userData.lastName}`,
          reason: displayReason
        });
        
        logger.log('✅ Red email bildirimi gönderildi');
      } catch (emailError) {
        logger.warn('⚠️ Email gönderilemedi (fonksiyon henüz deploy edilmemiş olabilir):', emailError.message);
        // Email hatası tüm işlemi engellemez
      }
    }
    
    // Satıcıya bildirim gönder
    try {
      await createNotification(
        sellerId,
        NOTIFICATION_TYPES.SELLER_REJECTED,
        'Satıcı Başvurunuz Reddedildi',
        noteVisibleToUser && reason
          ? `Başvurunuz reddedildi. Sebep: ${reason}`
          : 'Başvurunuz reddedildi. Detaylar için e-postanızı kontrol edin.',
        { link: '/become-seller' }
      );
    } catch (notifError) {
      logger.warn('Satıcı red bildirimi gönderilemedi:', notifError.message);
    }

    logger.log(`❌ Satıcı başvurusu reddedildi`);
    return { success: true };
  } catch (error) {
    logger.error('❌ Satıcı reddedilirken hata:', error);
    return { success: false, error: error.message };
  }
};

/**
 * İstatistikleri getirir
 */
export const getStats = async () => {
  try {
    logger.log('📊 İstatistikler getiriliyor...');
    
    // Kullanıcı sayısı
    const usersSnapshot = await getDocs(collection(db, 'users'));
    const totalUsers = usersSnapshot.size;
    
    // Satıcı sayısı
    const sellersSnapshot = await getDocs(collection(db, 'sellerProfiles'));
    const totalSellers = sellersSnapshot.size;
    
    // Aktif satıcı sayısı
    const activeSellers = sellersSnapshot.docs.filter(doc => doc.data().isActive === true).length;
    
    // Sipariş sayısı
    const ordersSnapshot = await getDocs(collection(db, 'orders'));
    const totalOrders = ordersSnapshot.size;
    
    // Ürün sayısı (products collection)
    const productsSnapshot = await getDocs(collection(db, 'products'));
    const totalModels = productsSnapshot.size;
    
    // Onaylanan ürün sayısı
    const approvedModels = productsSnapshot.docs.filter(doc => doc.data().approvalStatus === 'approved').length;
    
    // Bekleyen satıcı başvuruları
    const pendingSellersSnapshot = await getDocs(
      query(collection(db, 'sellerProfiles'), where('approvalStatus', '==', 'pending'))
    );
    const pendingSellers = pendingSellersSnapshot.size;
    
    const stats = {
      totalUsers,
      totalSellers,
      activeSellers,
      pendingSellers,
      totalOrders,
      totalModels,
      approvedModels
    };
    
    logger.log('✅ İstatistikler:', stats);
    return { success: true, data: stats };
  } catch (error) {
    logger.error('❌ İstatistikler getirilirken hata:', error);
    return { success: false, error: error.message, data: null };
  }
};

/**
 * Onay bekleyen satıcı profil görsellerini getirir
 */
export const getPendingProfileImages = async () => {
  try {
    const q = query(
      collection(db, 'sellerProfiles'),
      where('profileImageApprovalStatus', '==', 'pending')
    );
    
    const querySnapshot = await getDocs(q);
    const pending = [];
    
    for (const docSnap of querySnapshot.docs) {
      const data = docSnap.data();
      // Kullanıcı bilgilerini al
      const userDoc = await getDoc(doc(db, 'users', docSnap.id));
      const userData = userDoc.exists() ? userDoc.data() : {};
      
      pending.push({
        id: docSnap.id,
        ...data,
        userName: `${userData.firstName || ''} ${userData.lastName || ''}`.trim(),
        userEmail: userData.email
      });
    }
    
    return { success: true, data: pending };
  } catch (error) {
    logger.error('Pending profile images error:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Satıcı profil görselini onayla
 */
export const approveProfileImage = async (sellerId, adminId) => {
  try {
    const sellerRef = doc(db, 'sellerProfiles', sellerId);
    const sellerSnap = await getDoc(sellerRef);
    
    if (!sellerSnap.exists()) {
      return { success: false, error: 'Satıcı profili bulunamadı' };
    }
    
    const sellerData = sellerSnap.data();
    const pendingUrl = sellerData.pendingProfileImageUrl;
    
    // Eski görseli sil (varsa ve pending'den farklıysa)
    if (sellerData.profileImageUrl && sellerData.profileImageUrl !== pendingUrl) {
      try {
        const oldPath = sellerData.profileImageUrl.split('/o/')[1]?.split('?')[0];
        if (oldPath) {
          await deleteFile(decodeURIComponent(oldPath));
        }
      } catch (err) {
        logger.log('Eski görsel silinemedi:', err);
      }
    }
    
    // Pending görseli aktif yap
    await updateDoc(sellerRef, {
      profileImageUrl: pendingUrl,
      pendingProfileImageUrl: null,
      profileImageApprovalStatus: 'approved',
      profileImageApprovedBy: adminId,
      profileImageApprovedAt: new Date().toISOString()
    });

    // Denetim kaydı: profil görseli onayı (hangi görsel onaylandı)
    await recordApprovalLog({
      entityType: AUDIT_ENTITY.PROFILE_IMAGE,
      entityId: sellerId,
      action: AUDIT_ACTION.APPROVED,
      actorId: adminId,
      sellerId,
      before: { profileImageUrl: sellerData.profileImageUrl ?? null },
      after: { profileImageUrl: pendingUrl ?? null },
      note: 'Profil görseli onaylandı'
    });

    // Satıcıya bildirim gönder
    try {
      await createNotification(
        sellerId,
        NOTIFICATION_TYPES.PROFILE_IMAGE_APPROVED,
        'Profil Görseliniz Onaylandı',
        'Yüklediğiniz profil görseli onaylandı ve artık aktif.',
        { link: '/my-store' }
      );
    } catch (notifError) {
      logger.warn('Profil görseli onay bildirimi gönderilemedi:', notifError.message);
    }

    return { success: true, message: 'Görsel başarıyla onaylandı!' };
  } catch (error) {
    logger.error('Approve profile image error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Satıcı profil görselini reddet
 * @param {boolean} noteVisibleToUser - Notu kullanıcıya göster
 */
export const rejectProfileImage = async (sellerId, adminId, reason = '', noteVisibleToUser = false) => {
  try {
    const sellerRef = doc(db, 'sellerProfiles', sellerId);
    const sellerSnap = await getDoc(sellerRef);
    if (!sellerSnap.exists()) return { success: false, error: 'Satıcı profili bulunamadı' };
    
    const sellerData = sellerSnap.data();
    if (sellerData.pendingProfileImageUrl) {
      try {
        const pendingPath = sellerData.pendingProfileImageUrl.split('/o/')[1]?.split('?')[0];
        if (pendingPath) await deleteFile(decodeURIComponent(pendingPath));
      } catch (err) { logger.log('Pending görsel silinemedi:', err); }
    }
    
    await updateDoc(sellerRef, {
      pendingProfileImageUrl: null,
      profileImageApprovalStatus: 'rejected',
      profileImageRejectedBy: adminId,
      profileImageRejectedAt: new Date().toISOString(),
      profileImageRejectionReason: reason,
      profileImageRejectionNoteVisibleToUser: !!noteVisibleToUser
    });

    // Denetim kaydı: profil görseli reddi (reddedilen görsel)
    await recordApprovalLog({
      entityType: AUDIT_ENTITY.PROFILE_IMAGE,
      entityId: sellerId,
      action: AUDIT_ACTION.REJECTED,
      actorId: adminId,
      sellerId,
      before: { pendingProfileImageUrl: sellerData.pendingProfileImageUrl ?? null },
      note: reason || 'Profil görseli reddedildi'
    });
    // Satıcıya bildirim gönder
    try {
      await createNotification(
        sellerId,
        NOTIFICATION_TYPES.PROFILE_IMAGE_REJECTED,
        'Profil Görseliniz Reddedildi',
        noteVisibleToUser && reason
          ? `Yüklediğiniz profil görseli reddedildi. Sebep: ${reason}`
          : 'Yüklediğiniz profil görseli reddedildi. Lütfen yeni bir görsel yükleyin.',
        { link: '/my-store' }
      );
    } catch (notifError) {
      logger.warn('Profil görseli red bildirimi gönderilemedi:', notifError.message);
    }

    return { success: true, message: 'Görsel reddedildi!' };
  } catch (error) {
    logger.error('Reject profile image error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Onay bekleyen ürünleri getirir: yeni eklenen (approvalStatus === 'pending') ve düzenleme bekleyen (hasPendingEdit === true)
 */
export const getPendingProducts = async () => {
  try {
    const pending = [];
    const seenIds = new Set();

    const addWithSeller = async (docSnap) => {
      const data = docSnap.data();
      if (seenIds.has(docSnap.id)) return;
      seenIds.add(docSnap.id);
      const sellerDoc = await getDoc(doc(db, 'sellerProfiles', data.sellerId));
      const sellerData = sellerDoc.exists() ? sellerDoc.data() : {};
      pending.push({
        id: docSnap.id,
        ...data,
        sellerName: sellerData.publicName || 'Bilinmiyor'
      });
    };

    const qNew = query(
      collection(db, 'products'),
      where('approvalStatus', '==', 'pending'),
      orderBy('createdAt', 'desc')
    );
    const snapNew = await getDocs(qNew);
    for (const d of snapNew.docs) await addWithSeller(d);

    const qEdit = query(
      collection(db, 'products'),
      where('hasPendingEdit', '==', true),
      orderBy('pendingEditAt', 'desc')
    );
    try {
      const snapEdit = await getDocs(qEdit);
      for (const d of snapEdit.docs) await addWithSeller(d);
    } catch (err) {
      if (err?.code === 9 || err?.message?.includes('index')) {
        const snapEdit2 = await getDocs(query(collection(db, 'products'), where('hasPendingEdit', '==', true)));
        for (const d of snapEdit2.docs) await addWithSeller(d);
      } else throw err;
    }

    pending.sort((a, b) => {
      const dateA = a.hasPendingEdit ? (a.pendingEditAt?.toDate?.() || a.pendingEditAt) : (a.createdAt?.toDate?.() || a.createdAt);
      const dateB = b.hasPendingEdit ? (b.pendingEditAt?.toDate?.() || b.pendingEditAt) : (b.createdAt?.toDate?.() || b.createdAt);
      return new Date(dateB) - new Date(dateA);
    });

    return { success: true, data: pending };
  } catch (error) {
    logger.error('Pending products error:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Ürünü onayla: yeni ürün ise yayına alır; düzenleme talebi ise pending alanları asıl alanlara uygular
 */
export const approveProduct = async (productId, adminId) => {
  try {
    const productRef = doc(db, 'products', productId);
    const productSnap = await getDoc(productRef);
    if (!productSnap.exists()) {
      return { success: false, error: 'Ürün bulunamadı' };
    }
    const data = productSnap.data();

    // Self-approval engeli: kendi ürününüzü/düzenlemenizi onaylayamazsınız
    if (data.sellerId && data.sellerId === adminId) {
      return { success: false, error: 'Kendi ürününüzü/düzenlemenizi onaylayamazsınız. Farklı bir yetkili onaylamalı.' };
    }

    if (data.hasPendingEdit) {
      const updates = {
        name: data.pendingName,
        description: data.pendingDescription,
        category: data.pendingCategory,
        productType: data.pendingProductType,
        images: data.pendingImages || [],
        thumbnails: data.pendingThumbnails || [],
        fileUrl: data.pendingFileUrl ?? null,
        price: data.pendingPrice,
        stockCount: data.pendingStockCount ?? 0,
        inStock: data.pendingInStock !== false,
        specifications: data.pendingSpecifications || {},
        filamentType: data.pendingFilamentType ?? null,
        printQuality: data.pendingPrintQuality ?? 'normal',
        printTime: data.pendingPrintTime ?? null,
        materialWeight: data.pendingMaterialWeight ?? null,
        supportRequired: data.pendingSupportRequired === true,
        postProcessing: data.pendingPostProcessing || [],
        tags: data.pendingTags || [],
        hasPendingEdit: false,
        pendingEditAt: null,
        pendingName: null,
        pendingDescription: null,
        pendingCategory: null,
        pendingProductType: null,
        pendingImages: null,
        pendingThumbnails: null,
        pendingFileUrl: null,
        pendingPrice: null,
        pendingStockCount: null,
        pendingInStock: null,
        pendingSpecifications: null,
        pendingFilamentType: null,
        pendingPrintQuality: null,
        pendingPrintTime: null,
        pendingMaterialWeight: null,
        pendingSupportRequired: null,
        pendingPostProcessing: null,
        pendingTags: null,
        productEditRejectionReason: null,
        productEditRejectionNoteVisibleToUser: null,
        pendingPriceHistory: null,
        updatedAt: new Date().toISOString()
      };

      // Fiyat değişikliği varsa priceHistory'e kaydet
      if (data.pendingPriceHistory) {
        updates.priceHistory = arrayUnion({
          ...data.pendingPriceHistory,
          approvedBy: adminId,
          approvedAt: new Date().toISOString()
        });
      } else if (data.pendingPrice != null && data.price != null && data.pendingPrice !== data.price) {
        updates.priceHistory = arrayUnion({
          oldPrice: data.price,
          newPrice: data.pendingPrice,
          date: new Date().toISOString(),
          approvedBy: adminId,
          autoApproved: false
        });
      }

      await updateDoc(productRef, updates);

      // Denetim kaydı: düzenleme onayı (öncesi = canlı, sonrası = uygulanan pending)
      await recordApprovalLog({
        entityType: AUDIT_ENTITY.PRODUCT,
        entityId: productId,
        action: AUDIT_ACTION.APPROVED,
        actorId: adminId,
        sellerId: data.sellerId,
        before: {
          name: data.name, price: data.price, description: data.description, category: data.category,
          images: data.images || [], thumbnails: data.thumbnails || [], fileUrl: data.fileUrl ?? null,
          stockCount: data.stockCount ?? 0, inStock: data.inStock, tags: data.tags || []
        },
        after: {
          name: updates.name, price: updates.price, description: updates.description, category: updates.category,
          images: updates.images || [], thumbnails: updates.thumbnails || [], fileUrl: updates.fileUrl ?? null,
          stockCount: updates.stockCount ?? 0, inStock: updates.inStock, tags: updates.tags || []
        },
        note: 'Ürün düzenleme onayı'
      });

      // Satıcıya düzenleme onay bildirimi
      try {
        await createNotification(
          data.sellerId,
          NOTIFICATION_TYPES.PRODUCT_EDIT_APPROVED,
          'Ürün Düzenlemeniz Onaylandı',
          `"${data.pendingName || data.name}" ürününüzün düzenlemesi onaylandı.`,
          { link: `/product/${productId}`, productId }
        );
      } catch (notifError) {
        logger.warn('Ürün düzenleme onay bildirimi gönderilemedi:', notifError.message);
      }

      return { success: true, message: 'Ürün düzenlemesi onaylandı!' };
    }

    await updateDoc(productRef, {
      approvalStatus: 'approved',
      approvedBy: adminId,
      approvedAt: new Date().toISOString()
    });

    // Denetim kaydı: yeni ürün onayı
    await recordApprovalLog({
      entityType: AUDIT_ENTITY.PRODUCT,
      entityId: productId,
      action: AUDIT_ACTION.APPROVED,
      actorId: adminId,
      sellerId: data.sellerId,
      after: {
        name: data.name, price: data.price, category: data.category,
        images: data.images || [], description: data.description
      },
      note: 'Yeni ürün onayı'
    });

    // Satıcıya ürün onay bildirimi
    try {
      await createNotification(
        data.sellerId,
        NOTIFICATION_TYPES.PRODUCT_APPROVED,
        'Ürününüz Onaylandı!',
        `"${data.name}" adlı ürününüz onaylandı ve artık satışta.`,
        { link: `/product/${productId}`, productId }
      );
    } catch (notifError) {
      logger.warn('Ürün onay bildirimi gönderilemedi:', notifError.message);
    }

    return { success: true, message: 'Ürün başarıyla onaylandı!' };
  } catch (error) {
    logger.error('Approve product error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Ürünü reddet: düzenleme talebi ise sadece pending kaldırılır; yeni ürün ise reddedilir
 * @param {boolean} noteVisibleToUser - Notu kullanıcıya göster
 */
export const rejectProduct = async (productId, adminId, reason = '', noteVisibleToUser = false) => {
  try {
    const productRef = doc(db, 'products', productId);
    const productSnap = await getDoc(productRef);

    if (!productSnap.exists()) {
      return { success: false, error: 'Ürün bulunamadı' };
    }

    const productData = productSnap.data();

    if (productData.hasPendingEdit) {
      await updateDoc(productRef, {
        hasPendingEdit: false,
        pendingEditAt: null,
        pendingName: null,
        pendingDescription: null,
        pendingCategory: null,
        pendingProductType: null,
        pendingImages: null,
        pendingThumbnails: null,
        pendingFileUrl: null,
        pendingPrice: null,
        pendingStockCount: null,
        pendingInStock: null,
        pendingSpecifications: null,
        pendingFilamentType: null,
        pendingPrintQuality: null,
        pendingPrintTime: null,
        pendingMaterialWeight: null,
        pendingSupportRequired: null,
        pendingPostProcessing: null,
        pendingTags: null,
        productEditRejectionReason: reason || null,
        productEditRejectionNoteVisibleToUser: !!noteVisibleToUser,
        updatedAt: new Date().toISOString()
      });

      // Denetim kaydı: düzenleme reddi (reddedilen pending alanlar kayda alınır)
      await recordApprovalLog({
        entityType: AUDIT_ENTITY.PRODUCT,
        entityId: productId,
        action: AUDIT_ACTION.REJECTED,
        actorId: adminId,
        sellerId: productData.sellerId,
        before: {
          name: productData.name, price: productData.price, images: productData.images || []
        },
        after: {
          name: productData.pendingName, price: productData.pendingPrice, images: productData.pendingImages || []
        },
        note: reason || 'Düzenleme reddedildi'
      });

      // Satıcıya düzenleme red bildirimi
      try {
        await createNotification(
          productData.sellerId,
          NOTIFICATION_TYPES.PRODUCT_EDIT_REJECTED,
          'Ürün Düzenlemeniz Reddedildi',
          noteVisibleToUser && reason
            ? `"${productData.name}" ürünündeki düzenlemeniz reddedildi. Sebep: ${reason}`
            : `"${productData.name}" ürünündeki düzenlemeniz reddedildi. Mevcut bilgiler korundu.`,
          { link: `/product/${productId}`, productId }
        );
      } catch (notifError) {
        logger.warn('Ürün düzenleme red bildirimi gönderilemedi:', notifError.message);
      }

      return { success: true, message: 'Ürün düzenlemesi reddedildi. Mevcut bilgiler aynen kaldı.' };
    }

    if (productData.images && productData.images.length > 0) {
      for (const imageUrl of productData.images) {
        try {
          const imagePath = imageUrl.split('/o/')[1]?.split('?')[0];
          if (imagePath) await deleteFile(decodeURIComponent(imagePath));
        } catch (err) { logger.log('Ürün görseli silinemedi:', err); }
      }
    }

    await updateDoc(productRef, {
      approvalStatus: 'rejected',
      rejectedBy: adminId,
      rejectedAt: new Date().toISOString(),
      rejectionReason: reason,
      rejectionNoteVisibleToUser: !!noteVisibleToUser
    });

    // Denetim kaydı: yeni ürün reddi
    await recordApprovalLog({
      entityType: AUDIT_ENTITY.PRODUCT,
      entityId: productId,
      action: AUDIT_ACTION.REJECTED,
      actorId: adminId,
      sellerId: productData.sellerId,
      before: { name: productData.name, price: productData.price, images: productData.images || [] },
      note: reason || 'Ürün reddedildi'
    });

    // Satıcıya ürün red bildirimi
    try {
      await createNotification(
        productData.sellerId,
        NOTIFICATION_TYPES.PRODUCT_REJECTED,
        'Ürününüz Reddedildi',
        noteVisibleToUser && reason
          ? `"${productData.name}" adlı ürününüz reddedildi. Sebep: ${reason}`
          : `"${productData.name}" adlı ürününüz reddedildi.`,
        { link: '/my-store' }
      );
    } catch (notifError) {
      logger.warn('Ürün red bildirimi gönderilemedi:', notifError.message);
    }

    return { success: true, message: 'Ürün reddedildi!' };
  } catch (error) {
    logger.error('Reject product error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Sayfa görünürlük ayarlarını getirir
 * localStorage cache kullanarak ilk render'da anında gösterim sağlar
 */
export const getPageVisibilitySettings = async () => {
  // Varsayılan ayarlar - Firestore erişimi başarısız olursa bunları kullan
  const defaultSettings = {
    home: true,
    marketplace: true,
    sellers: true,
    about: true,
    contact: true,
    becomeSeller: true,
    photoTo3D: true,
  };

  // Önce localStorage'dan cache'i kontrol et (ilk render için)
  const cacheKey = 'pageVisibilitySettings';
  const cacheTimestampKey = 'pageVisibilitySettingsTimestamp';
  const cacheMaxAge = 5 * 60 * 1000; // 5 dakika
  
  try {
    const cachedData = localStorage.getItem(cacheKey);
    const cachedTimestamp = localStorage.getItem(cacheTimestampKey);
    
    // Cache varsa ve tazeyse kullan
    if (cachedData && cachedTimestamp) {
      const age = Date.now() - parseInt(cachedTimestamp, 10);
      if (age < cacheMaxAge) {
        try {
          const parsedCache = JSON.parse(cachedData);
          // Cache'i hemen döndür ama arka planda Firebase'den güncelle
          setTimeout(async () => {
            try {
              const settingsRef = doc(db, 'settings', 'pageVisibility');
              const settingsDoc = await getDoc(settingsRef);
              if (settingsDoc.exists()) {
                const freshData = settingsDoc.data();
                localStorage.setItem(cacheKey, JSON.stringify(freshData));
                localStorage.setItem(cacheTimestampKey, Date.now().toString());
                // Event tetikle ki navbar güncellensin
                if (typeof window !== 'undefined') {
                  window.dispatchEvent(new CustomEvent('pageVisibilitySettingsUpdated', {
                    detail: { settings: freshData }
                  }));
                }
              }
            } catch (err) {
              // Sessizce devam et, cache kullanılmaya devam edecek
            }
          }, 0);
          return { success: true, data: parsedCache };
        } catch (e) {
          // Cache parse hatası, devam et Firebase'den çek
        }
      }
    }
  } catch (e) {
    // localStorage hatası, devam et Firebase'den çek
  }

  // Firebase'den çek
  try {
    const settingsRef = doc(db, 'settings', 'pageVisibility');
    const settingsDoc = await getDoc(settingsRef);
    
    if (settingsDoc.exists()) {
      const data = settingsDoc.data();
      // Cache'e kaydet
      try {
        localStorage.setItem(cacheKey, JSON.stringify(data));
        localStorage.setItem(cacheTimestampKey, Date.now().toString());
      } catch (e) {
        // localStorage hatası, devam et
      }
      return { success: true, data };
    }
    
    // Döküman yoksa varsayılan ayarları döndür ve cache'e kaydet
    try {
      localStorage.setItem(cacheKey, JSON.stringify(defaultSettings));
      localStorage.setItem(cacheTimestampKey, Date.now().toString());
    } catch (e) {
      // localStorage hatası, devam et
    }
    return { success: true, data: defaultSettings };
  } catch (error) {
    // Firestore erişim hatası durumunda cache'i kullan
    try {
      const cachedData = localStorage.getItem(cacheKey);
      if (cachedData) {
        const parsedCache = JSON.parse(cachedData);
        return { success: true, data: parsedCache };
      }
    } catch (e) {
      // Cache de yoksa varsayılan ayarları kullan
    }
    return { success: true, data: defaultSettings };
  }
};

// Not: Real-time listener kaldırıldı, performans için event-based yaklaşım kullanılıyor
// subscribeToPageVisibilitySettings fonksiyonu artık kullanılmıyor

/**
 * Sayfa görünürlük ayarlarını günceller
 */
export const updatePageVisibilitySettings = async (settings) => {
  try {
    const settingsRef = doc(db, 'settings', 'pageVisibility');
    await setDoc(settingsRef, settings, { merge: true });
    
    // Cache'i güncelle
    try {
      localStorage.setItem('pageVisibilitySettings', JSON.stringify(settings));
      localStorage.setItem('pageVisibilitySettingsTimestamp', Date.now().toString());
    } catch (e) {
      // localStorage hatası, devam et
    }
    
    // Custom event tetikle - tüm açık sayfalardaki navbar'lar güncellenecek
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('pageVisibilitySettingsUpdated', {
        detail: { settings }
      }));
    }
    
    return { success: true, message: 'Sayfa görünürlük ayarları güncellendi!' };
  } catch (error) {
    logger.error('Update page visibility error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Bakım modu ayarlarını getirir (herkes okuyabilir - bakım ekranı için)
 * @param {boolean} bypassCache - true ise cache atlanır, Firestore'dan taze veri çekilir (farklı sekme/cihaz için)
 */
export const getMaintenanceSettings = async (bypassCache = false) => {
  const defaultSettings = {
    enabled: false,
    message: '',
    updatedAt: null
  };

  const cacheKey = 'maintenanceSettings';
  const cacheTimestampKey = 'maintenanceSettingsTimestamp';
  const cacheMaxAge = 1 * 60 * 1000; // 1 dakika

  if (!bypassCache) {
    try {
      const cachedData = localStorage.getItem(cacheKey);
      const cachedTimestamp = localStorage.getItem(cacheTimestampKey);
      if (cachedData && cachedTimestamp) {
        const age = Date.now() - parseInt(cachedTimestamp, 10);
        if (age < cacheMaxAge) {
          try {
            const parsed = JSON.parse(cachedData);
            return { success: true, data: parsed };
          } catch (e) {
            // parse hatası, Firestore'dan çek
          }
        }
      }
    } catch (e) {
      // localStorage hatası
    }
  }

  try {
    const settingsRef = doc(db, 'settings', 'maintenance');
    const settingsDoc = await getDoc(settingsRef);
    const data = settingsDoc.exists() ? settingsDoc.data() : defaultSettings;
    try {
      localStorage.setItem(cacheKey, JSON.stringify(data));
      localStorage.setItem(cacheTimestampKey, Date.now().toString());
    } catch (e) {}
    return { success: true, data: { ...defaultSettings, ...data } };
  } catch (error) {
    try {
      const cachedData = localStorage.getItem(cacheKey);
      if (cachedData) {
        return { success: true, data: JSON.parse(cachedData) };
      }
    } catch (e) {}
    return { success: true, data: defaultSettings };
  }
};

/**
 * Bakım modunu açar/kapatır (sadece admin çağırmalı)
 * @param {boolean} enabled - true = bakımda, false = normal
 * @param {string} [message] - Bakım ekranında gösterilecek mesaj
 */
export const setMaintenanceMode = async (enabled, message = '') => {
  try {
    const settingsRef = doc(db, 'settings', 'maintenance');
    const payload = {
      enabled: !!enabled,
      message: typeof message === 'string' ? message.trim() : '',
      updatedAt: new Date().toISOString()
    };
    await setDoc(settingsRef, payload, { merge: true });

    try {
      localStorage.setItem('maintenanceSettings', JSON.stringify(payload));
      localStorage.setItem('maintenanceSettingsTimestamp', Date.now().toString());
    } catch (e) {}
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('maintenanceSettingsUpdated', { detail: { settings: payload } }));
    }
    return { success: true, message: enabled ? 'Site bakım moduna alındı.' : 'Bakım modu kapatıldı.' };
  } catch (error) {
    logger.error('Set maintenance mode error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Geri bildirim sistemi ayarını getirir
 */
export const getFeedbackSettings = async () => {
  const defaultSettings = {
    enabled: true
  };

  try {
    const settingsRef = doc(db, 'settings', 'feedback');
    const settingsDoc = await getDoc(settingsRef);
    
    if (settingsDoc.exists()) {
      return { success: true, data: settingsDoc.data() };
    }
    
    return { success: true, data: defaultSettings };
  } catch (error) {
    return { success: true, data: defaultSettings };
  }
};

/**
 * Geri bildirim sistemi ayarını günceller
 */
export const updateFeedbackSettings = async (settings) => {
  try {
    const settingsRef = doc(db, 'settings', 'feedback');
    await setDoc(settingsRef, settings, { merge: true });
    
    return { success: true, message: 'Geri bildirim ayarları güncellendi!' };
  } catch (error) {
    logger.error('Update feedback settings error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Bakım ayarlarını gerçek zamanlı dinler (F5 gerektirmez)
 * @param {(data: { enabled: boolean, message: string }) => void} callback
 * @returns {() => void} unsubscribe
 */
export const subscribeMaintenanceSettings = (callback) => {
  const settingsRef = doc(db, 'settings', 'maintenance');
  const defaultData = { enabled: false, message: '' };
  return onSnapshot(settingsRef, (snap) => {
    const data = snap.exists() ? { ...defaultData, ...snap.data() } : defaultData;
    callback({ enabled: !!data.enabled, message: data.message || '' });
  }, (error) => {
    logger.error('Bakım ayarları dinlenirken hata:', error);
    callback(defaultData);
  });
};

/**
 * Geri bildirim ayarlarını gerçek zamanlı dinler (F5 gerektirmez)
 * @param {(data: { enabled: boolean }) => void} callback
 * @returns {() => void} unsubscribe
 */
export const subscribeFeedbackSettings = (callback) => {
  const settingsRef = doc(db, 'settings', 'feedback');
  return onSnapshot(settingsRef, (snap) => {
    const data = snap.exists() ? snap.data() : {};
    callback({ enabled: data.enabled !== false });
  }, (error) => {
    logger.error('Geri bildirim ayarları dinlenirken hata:', error);
    callback({ enabled: true });
  });
};

/**
 * Moderator erişim ayarlarını getirir
 */
export const getModeratorPermissions = async () => {
  try {
    const settingsRef = doc(db, 'settings', 'moderatorPermissions');
    const settingsDoc = await getDoc(settingsRef);
    
    if (settingsDoc.exists()) {
      return { success: true, data: settingsDoc.data() };
    }
    
    // Varsayılan moderator izinleri
    const defaultPermissions = {
      // Satıcı İşlemleri
      canApproveSellers: true,
      canRejectSellers: true,
      canToggleSellerStatus: true,
      
      // İçerik Onayları
      canApproveProducts: true,
      canRejectProducts: true,
      canApproveImages: true,
      canRejectImages: true,
      
      // Sipariş İşlemleri
      canViewOrders: true,
      canUpdateOrderStatus: true,
      
      // Ürün İşlemleri
      canViewProducts: true,
      canViewStats: true,
      
      // İletişim
      canViewMessages: true,
      canUpdateMessages: true,
      
      // Satıcı Detayları
      canViewSellerDetails: true,
      
      // Dashboard
      canViewDashboard: true,
    };
    
    // Varsayılan ayarları kaydet
    try {
      await updateDoc(settingsRef, defaultPermissions);
    } catch {
      // Doc yoksa oluştur
      await setDoc(settingsRef, defaultPermissions);
    }
    
    return { success: true, data: defaultPermissions };
  } catch (error) {
    logger.error('Moderator permissions error:', error);
    return { success: false, error: error.message, data: {} };
  }
};

/**
 * Moderator erişim ayarlarını günceller (sadece admin)
 */
export const updateModeratorPermissions = async (permissions) => {
  try {
    const settingsRef = doc(db, 'settings', 'moderatorPermissions');
    await setDoc(settingsRef, permissions, { merge: true });
    
    return { success: true, message: 'Moderator erişim ayarları güncellendi!' };
  } catch (error) {
    logger.error('Update moderator permissions error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Tüm geri bildirimleri getirir
 */
export const getAllFeedback = async () => {
  try {
    const feedbackRef = collection(db, 'feedback');
    const q = query(feedbackRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);

    const feedbacks = [];
    querySnapshot.forEach((doc) => {
      feedbacks.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return { success: true, data: feedbacks };
  } catch (error) {
    logger.error('Get all feedback error:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Geri bildirimi okundu olarak işaretle
 * @param {string} feedbackId - Geri bildirim ID
 * @param {Object} options - Opsiyonel: { userId, userEmail, isModerator }
 *   - isModerator: true ise moderator okudu olarak işaretlenir, hangi moderator okudu kaydedilir
 */
export const markFeedbackAsRead = async (feedbackId, options = {}) => {
  try {
    const { userId, userEmail, isModerator } = options;
    const feedbackRef = doc(db, 'feedback', feedbackId);

    const updateData = {
      read: true,
      status: isModerator ? 'moderator_read' : 'read'
    };

    if (userId) {
      if (isModerator) {
        updateData.moderatorReadBy = userId;
        updateData.moderatorReadByEmail = userEmail || null;
        updateData.moderatorReadAt = serverTimestamp();
      } else {
        updateData.adminReadBy = userId;
        updateData.adminReadByEmail = userEmail || null;
        updateData.adminReadAt = serverTimestamp();
      }
    }

    await updateDoc(feedbackRef, updateData);
    
    return { success: true, message: 'Geri bildirim okundu olarak işaretlendi!' };
  } catch (error) {
    logger.error('Mark feedback as read error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Geri bildirimi çözüldü olarak işaretle
 */
export const markFeedbackAsResolved = async (feedbackId) => {
  try {
    const feedbackRef = doc(db, 'feedback', feedbackId);
    await updateDoc(feedbackRef, {
      status: 'resolved'
    });
    
    return { success: true, message: 'Geri bildirim çözüldü olarak işaretlendi!' };
  } catch (error) {
    logger.error('Mark feedback as resolved error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Geri bildirimi sil
 */
export const deleteFeedback = async (feedbackId) => {
  try {
    const feedbackRef = doc(db, 'feedback', feedbackId);
    await deleteDoc(feedbackRef);

    return { success: true, message: 'Geri bildirim silindi!' };
  } catch (error) {
    logger.error('Delete feedback error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Tüm iletişim formu mesajlarını getirir (admin/moderator).
 * contactMessages koleksiyonu sendContactFormEmail Cloud Function tarafından yazılır;
 * e-posta gönderimi başarısız olsa bile mesaj burada görünür.
 */
export const getAllContactMessages = async () => {
  try {
    const ref = collection(db, 'contactMessages');
    const q = query(ref, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);

    const messages = [];
    querySnapshot.forEach((d) => {
      messages.push({ id: d.id, ...d.data() });
    });

    return { success: true, data: messages };
  } catch (error) {
    logger.error('Get all contact messages error:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * İletişim mesajını okundu olarak işaretle
 * @param {string} messageId
 * @param {Object} options - { userId, userEmail, isModerator }
 */
export const markContactMessageAsRead = async (messageId, options = {}) => {
  try {
    const { userId, userEmail, isModerator } = options;
    const ref = doc(db, 'contactMessages', messageId);

    const updateData = {
      read: true,
      status: isModerator ? 'moderator_read' : 'read'
    };

    if (userId) {
      if (isModerator) {
        updateData.moderatorReadBy = userId;
        updateData.moderatorReadByEmail = userEmail || null;
        updateData.moderatorReadAt = serverTimestamp();
      } else {
        updateData.adminReadBy = userId;
        updateData.adminReadByEmail = userEmail || null;
        updateData.adminReadAt = serverTimestamp();
      }
    }

    await updateDoc(ref, updateData);
    return { success: true, message: 'Mesaj okundu olarak işaretlendi!' };
  } catch (error) {
    logger.error('Mark contact message as read error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * İletişim mesajını cevaplandı/çözüldü olarak işaretle
 */
export const markContactMessageAsResolved = async (messageId) => {
  try {
    const ref = doc(db, 'contactMessages', messageId);
    await updateDoc(ref, { status: 'resolved' });
    return { success: true, message: 'Mesaj cevaplandı olarak işaretlendi!' };
  } catch (error) {
    logger.error('Mark contact message as resolved error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * İletişim mesajını sil (sadece admin)
 */
export const deleteContactMessage = async (messageId) => {
  try {
    const ref = doc(db, 'contactMessages', messageId);
    await deleteDoc(ref);
    return { success: true, message: 'Mesaj silindi!' };
  } catch (error) {
    logger.error('Delete contact message error:', error);
    return { success: false, error: error.message };
  }
};

/** Model galerisi sol/sağ reklam alanları varsayılan değer (mediaType: 'image' | 'video') */
const DEFAULT_MARKETPLACE_ADS = {
  left: { enabled: false, imageUrl: '', link: '', mediaType: 'image' },
  right: { enabled: false, imageUrl: '', link: '', mediaType: 'image' }
};

/**
 * Model galerisi reklam ayarlarını getirir (sol ve sağ alan)
 */
export const getMarketplaceAdSettings = async () => {
  try {
    const ref = doc(db, 'settings', 'marketplaceAds');
    const snap = await getDoc(ref);
    if (snap.exists()) {
      const data = snap.data();
      return {
        success: true,
        data: {
          left: { ...DEFAULT_MARKETPLACE_ADS.left, ...data.left },
          right: { ...DEFAULT_MARKETPLACE_ADS.right, ...data.right }
        }
      };
    }
    return { success: true, data: { ...DEFAULT_MARKETPLACE_ADS } };
  } catch (error) {
    logger.error('getMarketplaceAdSettings error:', error);
    return { success: false, error: error.message, data: { ...DEFAULT_MARKETPLACE_ADS } };
  }
};

/**
 * Model galerisi reklam ayarlarını günceller
 * @param {{ left?: { enabled?: boolean, imageUrl?: string, link?: string }, right?: { enabled?: boolean, imageUrl?: string, link?: string } }} settings
 */
export const setMarketplaceAdSettings = async (settings) => {
  try {
    const ref = doc(db, 'settings', 'marketplaceAds');
    const payload = {
      left: { ...DEFAULT_MARKETPLACE_ADS.left, ...settings.left },
      right: { ...DEFAULT_MARKETPLACE_ADS.right, ...settings.right },
      updatedAt: new Date().toISOString()
    };
    await setDoc(ref, payload, { merge: true });
    return { success: true, message: 'Reklam ayarları kaydedildi.' };
  } catch (error) {
    logger.error('setMarketplaceAdSettings error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Model galerisi reklam ayarlarını gerçek zamanlı dinler
 * @param {(data: { left: { enabled: boolean, imageUrl: string, link: string }, right: { enabled: boolean, imageUrl: string, link: string } }) => void} callback
 * @returns {() => void} unsubscribe
 */
export const subscribeMarketplaceAdSettings = (callback) => {
  const ref = doc(db, 'settings', 'marketplaceAds');
  return onSnapshot(
    ref,
    (snap) => {
      const data = snap.exists() ? snap.data() : {};
      callback({
        left: { ...DEFAULT_MARKETPLACE_ADS.left, ...data.left },
        right: { ...DEFAULT_MARKETPLACE_ADS.right, ...data.right }
      });
    },
    (err) => {
      logger.error('subscribeMarketplaceAdSettings error:', err);
      // Hata durumunda callback çağırma: önceki veri kalsın, reklamlar aniden kaybolmasın
    }
  );
};